// The one requestAnimationFrame loop: clock → sample → updates (world, director, hud) → render.
// The race timeline job replaces the stub provider with its RaceTimeline; nothing else changes.
import type { RaceEvent, RaceSnapshot, RaceTimeline } from '../race/types';

/** The part of RaceTimeline the loop needs. */
export type SnapshotProvider = Pick<RaceTimeline, 'sample' | 'eventsBetween' | 'peek'>;

export interface FrameContext {
  snapshot: RaceSnapshot;
  events: RaceEvent[]; // fired between the previous frame and this one
  peek: RaceEvent[]; // look-ahead, 2 s
  dt: number; // seconds, clamped
  dtMs: number; // real frame time, unclamped
  renderT: number; // signal-clock ms
  timeS: number; // wall-clock seconds since start
}

export type FrameUpdate = (ctx: FrameContext) => void;

export interface Loop {
  /** Adds a per-frame step. Steps run in the order they were added; render last. */
  add(step: FrameUpdate): () => void;
  setProvider(p: SnapshotProvider): void;
  /** Maps wall-clock ms to render time on the signal clock. Default: elapsed ms × 1. */
  setClock(clock: (wallMs: number) => number): void;
  start(): void;
  stop(): void;
  /**
   * Loop time in ms since start: real elapsed time, or in fixed-step capture mode the frame count
   * times the step. Sources that must keep pace with render time (the replay) read their clock here.
   */
  now(): number;
}

export interface LoopOptions {
  /**
   * Capture mode: every rendered frame advances time by exactly this many ms, however long the frame
   * really took, so a slow headless browser renders what a viewer sees at 1000 / fixedStepMs fps.
   */
  fixedStepMs?: number;
}

export const PEEK_MS = 2000;
const MAX_DT = 0.1;

export function createLoop(provider: SnapshotProvider, opts: LoopOptions = {}): Loop {
  const fixed = opts.fixedStepMs && opts.fixedStepMs > 0 ? opts.fixedStepMs : 0;
  const steps: FrameUpdate[] = [];
  let p = provider;
  let clock = (wallMs: number) => wallMs;
  let raf = 0;
  let t0 = -1;
  let last = 0;
  let lastRenderT = Number.NaN;
  let frames = 0;
  let realT0 = -1;

  const frame = (realNow: number) => {
    raf = requestAnimationFrame(frame);
    if (realT0 < 0) realT0 = realNow;
    // In capture mode, loop time is the frame count times the step; the first frame is time 0.
    const now = fixed ? frames++ * fixed : realNow;
    if (t0 < 0) { t0 = now; last = fixed ? now - fixed : now; }
    const dtMs = now - last;
    last = now;
    const renderT = clock(now - t0);
    const snapshot = p.sample(renderT);
    const events = Number.isNaN(lastRenderT) || renderT < lastRenderT ? [] : p.eventsBetween(lastRenderT, renderT);
    lastRenderT = renderT;
    const ctx: FrameContext = {
      snapshot, events, peek: p.peek(renderT, PEEK_MS),
      dt: Math.min(MAX_DT, dtMs / 1000), dtMs, renderT, timeS: (now - t0) / 1000,
    };
    for (const s of steps) s(ctx);
  };

  return {
    add(step) { steps.push(step); return () => { const i = steps.indexOf(step); if (i >= 0) steps.splice(i, 1); }; },
    setProvider(np) { p = np; lastRenderT = Number.NaN; },
    setClock(c) { clock = c; lastRenderT = Number.NaN; },
    start() { if (!raf) raf = requestAnimationFrame(frame); },
    stop() { cancelAnimationFrame(raf); raf = 0; t0 = -1; },
    now() {
      // Never runs backwards: a restart keeps counting frames.
      if (fixed) return Math.max(0, frames - 1) * fixed;
      return realT0 < 0 ? 0 : performance.now() - realT0;
    },
  };
}
